import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FaSearch } from "react-icons/fa";
import "../common/style.css";
import "./cv.css";
import { useNavigate, useParams } from "react-router-dom";
import { downloadpublicFile, getPublicCvs } from "../http/api";
import Loading from "../loading/loading";
import { useAuth } from "../http/authcontex";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TopNavPublic from "../common/topnav-public";

export default function PublicShowCVs() {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const authContext = useAuth();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [totalPages, setTotalPages] = useState(0);
  const [data, setData] = useState([]);
  const { id } = useParams();

  // State for filters
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState({
    vertical: "",
    jobdesignation: "",
    name: "",
  });

  // Function to fetch CV data with all filters
  const fetchCVData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPublicCvs(
        id,
        pagination.pageIndex,
        pagination.pageSize,
        globalFilter,
        columnFilters.vertical,
        columnFilters.name,
        columnFilters.jobdesignation
      );

      if (response.status === 200) {
        const { content, totalPages: total } = response.data;
        setData(content);
        setTotalPages(total);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        authContext.logout();
        navigate("");
      } else {
        console.error("Error fetching CV details:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [
    authContext,
    navigate,
    id,
    pagination.pageIndex,
    pagination.pageSize,
    globalFilter,
    columnFilters.vertical,
    columnFilters.name,
    columnFilters.jobdesignation,
  ]);

  // Use a separate effect to reset page when filters change

  // We only want to reset the page if a filter has changed.
  // The `pagination` state itself should not trigger this.
  // We can't directly check against old state, so a simple solution
  // is to ensure we're on page 0 whenever filters are applied.
  // If we're not on page 0, it means a filter change is happening, so reset.
  // Ref to track current pageIndex without causing effect to re-run
  const pageIndexRef = React.useRef(pagination.pageIndex);

  useEffect(() => {
    pageIndexRef.current = pagination.pageIndex; // keep ref updated
  }, [pagination.pageIndex]);

  useEffect(() => {
    if (pageIndexRef.current !== 0) {
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }
  }, [globalFilter, columnFilters.vertical, columnFilters.name, columnFilters.jobdesignation]);

  // This effect handles data fetching with a debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCVData();
    }, 500); // Debounce to avoid rapid API calls while typing

    return () => clearTimeout(timer);
  }, [fetchCVData, pagination.pageIndex, pagination.pageSize]); // Ensure fetch happens on page/size change

  const handleDownload = async (fileaname) => {
    try {
      setLoading(true);
      const file = fileaname;
      if (!file) return;

      if (file instanceof File) {
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
      } else if (typeof file === "string") {
        const response = await downloadpublicFile(file);
        if (response.status === 200 || response.status === 202) {
          setLoading(false);
          const byteArray = new Uint8Array(response.data);
          const blob = new Blob([byteArray], {
            type: response.headers["content-type"],
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = file;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          setLoading(false);
          toast.error("Failed to download file");
        }
      }
    } catch {
      setLoading(false);
      toast.error("Error downloading file");
    }
  };

  // Handle column filter changes
  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnId]: value,
    }));
  };

  // Define columns for TanStack Table
  const columns = useMemo(
    () => [
      {
        accessorKey: "vertical",
        header: "Vertical",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "jobdesignation",
        header: "Job Designation",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "name",
        header: "Name",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "fileName",
        header: "CV File",
        cell: ({ row }) => {
          const original = row.original;
          return original.fileName ? (
            <div className="d-flex gap-1">
              <button
                onClick={() => handleDownload(row.original.fileName)}
                className="btn btn-sm btn-success"
                title="Download CV"
              >
                Download
              </button>
            </div>
          ) : (
            <span>No File</span>
          );
        },
        meta: { className: "cv-file" },
        enableSorting: false,
      },
      {
        accessorKey: "lastModifiedDate",
        header: "Last Update",
        cell: (info) => {
          const date = info.getValue();
          if (!date) return "N/A";
          try {
            if (date instanceof Date) {
              return date.toLocaleString();
            }
            return new Date(date).toLocaleString();
          } catch (e) {
            console.error("Error parsing date:", date, e);
            return "N/A";
          }
        },
        meta: { className: "last-modified" },
      },
    ],
    []
  );

  // TanStack Table hook
  const table = useReactTable({
    data,
    columns,
    pageCount: totalPages,
    state: {
      pagination,
      globalFilter,
    },
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualFiltering: true,
  });

  return (
    <div className="ShowCVs">
      {loading && <Loading />}
      <TopNavPublic />
      <div id="layoutSidenav_content">
        <main>
          <div className="container-fluid px-2">
            <h4>CVs</h4>
            <hr className="bg-warning" style={{ height: "4px" }} />
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <input
                    type="text"
                    placeholder="Search all columns..."
                    className="form-control me-2"
                    value={globalFilter ?? ""}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                  />
                  <button className="btn btn-sm btn-secondary">
                    <FaSearch />
                  </button>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body shadow-sm border-0">
                <div className="table-responsive">
                  <table className="table table-striped table-hover table-sm">
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <React.Fragment key={headerGroup.id}>
                          {/* Column headers with sorting */}
                          <tr>
                            {headerGroup.headers.map((header) => (
                              <th key={header.id}>
                                {header.isPlaceholder ? null : (
                                  <div
                                    {...{
                                      className: header.column.getCanSort()
                                        ? "cursor-pointer select-none"
                                        : "",
                                      onClick:
                                        header.column.getToggleSortingHandler(),
                                    }}
                                  >
                                    {flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                                    {{
                                      asc: " 🔼",
                                      desc: " 🔽",
                                    }[header.column.getIsSorted()] ?? null}
                                  </div>
                                )}
                              </th>
                            ))}
                          </tr>
                          {/* Column filters */}
                          <tr>
                            {headerGroup.headers.map((header) => {
                              const columnId = header.column.id;
                              return (
                                <th key={header.id}>
                                  {["vertical", "jobdesignation", "name"].includes(columnId) ? (
                                    <input
                                      type="text"
                                      value={columnFilters[columnId] || ""}
                                      onChange={(e) =>
                                        handleColumnFilterChange(columnId, e.target.value)
                                      }
                                      placeholder={`Filter ${columnId}...`}
                                      className="form-control form-control-sm"
                                    />
                                  ) : null}
                                </th>
                              );
                            })}
                          </tr>
                        </React.Fragment>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.length ? (
                        table.getRowModel().rows.map((row) => (
                          <tr key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <td
                                key={cell.id}
                                className={
                                  cell.column.columnDef.meta?.className || ""
                                }
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columns.length} className="text-center">
                            No CV data available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      onClick={() => table.setPageIndex(0)}
                      disabled={!table.getCanPreviousPage()}
                    >
                      {"<<"}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      {"<"}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      {">"}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() =>
                        table.setPageIndex(table.getPageCount() - 1)
                      }
                      disabled={!table.getCanNextPage()}
                    >
                      {">>"}
                    </button>
                  </div>
                  <span className="d-flex align-items-center">
                    Page{" "}
                    <strong>
                      {table.getState().pagination.pageIndex + 1} of{" "}
                      {table.getPageCount()}
                    </strong>
                    {" | Go to page: "}
                    <input
                      type="number"
                      defaultValue={table.getState().pagination.pageIndex + 1}
                      onChange={(e) => {
                        const page = e.target.value
                          ? Number(e.target.value) - 1
                          : 0;
                        table.setPageIndex(page);
                      }}
                      className="form-control form-control-sm mx-2"
                      style={{ width: "70px" }}
                    />
                  </span>
                  <select
                    value={table.getState().pagination.pageSize}
                    onChange={(e) => {
                      table.setPageSize(Number(e.target.value));
                    }}
                    className="form-select form-select-sm"
                    style={{ width: "120px" }}
                  >
                    {[10, 20, 30, 40, 50].map((size) => (
                      <option key={size} value={size}>
                        Show {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}